import * as React from 'react';

import ConfirmDialog from './ConfirmDialog';
import { DialogComponentProps, showMessage } from './Dialog';
import { StyleButtonImageEditor, type StyleButtonImageEditorHandle } from './StyleButtonImageEditor';
import { StyleButtonImageSelector } from './StyleButtonImageSelector';
import { Convert } from 'core/utils';
import type { LoadedImage, StyleButtonMask } from './StyleButtonImagePickerTypes';
import { Res } from 'core/api';

type Props = DialogComponentProps<string> & {
	mask: StyleButtonMask;
	backgroundColor?: string;
	initialImageBase64?: string;
};

export function StyleButtonImagePickerDialog({
	mask,
	backgroundColor,
	initialImageBase64,
	actions,
}: Props) {
	const [loadedImage, setLoadedImage] = React.useState<LoadedImage | null>(null);
	const editorRef = React.useRef<StyleButtonImageEditorHandle>(null);

	React.useEffect(() => {
		if (!initialImageBase64) {
			setLoadedImage(null);
			return;
		}

		void (async () => {
			try {
				const src = `data:image/png;base64,${initialImageBase64}`;
				const img = await Convert.imageSrcToHTMLImage(src);
				setLoadedImage({
					src,
					naturalWidth: img.naturalWidth || img.width,
					naturalHeight: img.naturalHeight || img.height,
				});
			} catch (e) {
				void showMessage(Res.Msg.MSG_FAILED_TO_LOAD_IMAGE(e?.toString()));
			}
		})();
	}, [initialImageBase64]);

	const onImageSelected = React.useCallback((image: LoadedImage) => {
		setLoadedImage(image);
	}, []);

	const onChangeImage = React.useCallback(() => {
		setLoadedImage(null);
	}, []);

	const onSave = React.useCallback(async () => {
		if (!loadedImage || !editorRef.current) {
			void showMessage(Res.Msg.MSG_SELECT_IMAGE_BEFORE_SAVING());
			return;
		}
		actions?.closeWith(await editorRef.current.exportImage());
	}, [actions, loadedImage]);

	return <ConfirmDialog
		kind='base'
		title={'Seleccionar imagen'}
		onCancel={() => actions?.cancel()}
		onOK={() => void onSave()}
		acceptOnEnter={false}
	>
		{loadedImage
			? <StyleButtonImageEditor
				ref={editorRef}
				backgroundColor={backgroundColor}
				image={loadedImage}
				mask={mask}
				onChangeImage={onChangeImage}
			/>
			: <StyleButtonImageSelector
				onImageSelected={onImageSelected}
			/>}
	</ConfirmDialog>;
}

export type { StyleButtonMask };
